import type { AdminApiUser } from '@/types/serializers';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { LuPlus } from 'react-icons/lu';
import { useState } from 'react';
import useFetchAdminUsers from '../../api/useFetchAdminUsers';
import { AdminIconButton } from '../../components/AdminButton';
import AdminLoadingState from '../../components/molecules/AdminLoadingState';
import AdminErrorState from '../../components/molecules/AdminErrorState';
import AdminUserDeleteModal from '../../components/organisms/AdminUserDeleteModal';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { MdMoreVert } from 'react-icons/md';

const tableHeaders = ['氏名', 'メールアドレス', 'アクセス権限'];

const getRoleDisplay = (user: AdminApiUser) => {
  if (user.role === 'super_admin' || user.role === 'admin') {
    return { label: '管理者ユーザー', dotColor: 'bg-[#A866FF]' };
  }
  return { label: '通常ユーザー', dotColor: 'bg-[#4188FF]' };
};

export default function AdminUsersPage() {
  const { data, isLoading, isError } = useFetchAdminUsers();
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(null);

  if (isLoading) return <AdminLoadingState />;
  if (isError || !data) return <AdminErrorState message="ユーザー一覧の読み込みに失敗しました。" />;

  return (
    <div className="flex flex-col">
      <h1 className="my-0 text-2xl leading-[1.2] font-bold text-[#333]">ユーザー管理</h1>
      <div className="flex flex-col">
        <div className="flex items-center justify-end gap-4">
          <Link
            to="/admin"
            search={{ tab: 'users', userAction: 'new' }}
            className="flex items-center gap-1 rounded-full bg-[#4188FF] px-4 py-3 text-sm font-bold text-white no-underline hover:bg-[#3677e8]"
          >
            <LuPlus className="size-5" />
            <span>ユーザーを追加する</span>
          </Link>
        </div>
        <div className="mt-md overflow-hidden rounded border border-solid border-[#ccc] bg-white shadow-[0px_0px_6px_0px_rgba(0,0,0,0.2)]">
          <div className="flex border-0 border-b border-solid border-[#ccc] bg-[#F3F7FE]">
            {tableHeaders.map((header) => (
              <div
                key={header}
                className="flex flex-1 items-center py-2 pl-6 text-xs font-bold text-black"
              >
                {header}
              </div>
            ))}
            <div className="w-x3l shrink-0" />
          </div>
          <div className="flex flex-col">
            {data.map((user, index) => {
              const role = getRoleDisplay(user);
              return (
                <div
                  key={user.id}
                  className={clsx(
                    'flex h-12 border-0 border-b border-solid border-[#ddd] bg-white',
                    index === data.length - 1 && 'border-b-0'
                  )}
                >
                  <div className="flex flex-1 items-center overflow-hidden pl-6 text-sm font-bold text-[#333]">
                    {user.name}
                  </div>
                  <div className="flex flex-1 items-center overflow-hidden pl-6 text-xs text-[#333]">
                    {user.email}
                  </div>
                  <div className="flex flex-1 items-center gap-2 overflow-hidden pl-6">
                    <span className={clsx('size-2 shrink-0 rounded-full', role.dotColor)} />
                    <span className="text-sm font-bold text-[#333]">{role.label}</span>
                  </div>
                  <div className="flex w-x3l shrink-0 items-center justify-center">
                    <Popover>
                      <PopoverTrigger asChild>
                        <div>
                          <AdminIconButton
                            label="ユーザー操作"
                            icon={<MdMoreVert className="size-4" />}
                          />
                        </div>
                      </PopoverTrigger>
                      <PopoverContent
                        align="end"
                        className="w-[163px] rounded bg-white p-0 shadow-[0px_0px_12px_0px_rgba(0,0,0,0.2)]"
                      >
                        <div className="flex flex-col">
                          <Link
                            to="/admin"
                            search={{ tab: 'users', userId: user.id }}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm leading-[1.7] font-normal text-[#262626] no-underline hover:bg-[#F6F6F6]"
                          >
                            編集
                          </Link>
                          <button
                            type="button"
                            className="flex w-full cursor-pointer items-center gap-2 border-0 bg-transparent px-3 py-2 text-left text-sm leading-[1.7] font-normal text-[#FF4E4E] no-underline hover:bg-[#F6F6F6]"
                            onClick={() => {
                              setDeleteTarget({ id: user.id, name: user.name });
                            }}
                          >
                            削除
                          </button>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <p className="text-right text-xs leading-[1.2] text-[#333]">
          {data.length} アカウント作成済
        </p>
      </div>
      <AdminUserDeleteModal
        open={deleteTarget !== null}
        onClose={() => {
          setDeleteTarget(null);
        }}
        userId={deleteTarget?.id ?? null}
        userName={deleteTarget?.name ?? null}
      />
    </div>
  );
}
